// client/src/components/ContentHealthTable.js
// Model B master URL×check table — reads /api/campaigns/:id/health-matrix
// Ten check columns with Tabler icon headers, banded score cells,
// all ten drawers wired with full detail.
// Jun 24 2026
// Sep 4 2026 — added current HTTP Status gate and explicit not-auditable cells.
import React, { useState, useEffect } from 'react';
if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);
  }
}
const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  grey: '#e76f51',
  green: '#01796f'
};
function band(score) {
  if (score === null || score === undefined) return { bg: '#f3f4f6', text: '#9ca3af', bar: '#d1d5db' };
  if (score >= 70) return { bg: 'rgba(1, 121, 111, 0.08)', text: '#01796f', bar: '#01796f' };
  if (score >= 45) return { bg: 'rgba(233, 196, 106, 0.15)', text: '#854F0B', bar: '#e9c46a' };
  return { bg: 'rgba(231, 111, 81, 0.08)', text: '#993C1D', bar: '#e76f51' };
}
const CHECKS = [
  { key: 'schema',             label: 'Schema',              icon: 'ti-code' },
  { key: 'disambiguation',     label: 'Disambiguation',      icon: 'ti-fingerprint' },
  { key: 'crosslinking',       label: 'Cross-linking',       icon: 'ti-share' },
  { key: 'factDensity',        label: 'Fact density',        icon: 'ti-list-numbers' },
  { key: 'hallucinationRisk',  label: 'Hallucination risk',  icon: 'ti-alert-hexagon' },
  { key: 'headings',           label: 'Headings',            icon: 'ti-heading' },
  { key: 'structuredElements', label: 'Structured elements', icon: 'ti-layout-list' },
  { key: 'citationDensity',    label: 'Citation density',    icon: 'ti-quote' },
  { key: 'freshness',          label: 'Freshness',           icon: 'ti-clock' },
  { key: 'crawlability',       label: 'Crawlability',        icon: 'ti-world-check' }
];
function truncateUrl(url) {
  try {
    const { pathname } = new URL(url);
    return pathname.length > 55 ? '...' + pathname.slice(-52) : (pathname || '/');
  } catch { return url; }
}
function ScoreCell({ score }) {
  const b = band(score);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '36px', padding: '3px 7px', borderRadius: '6px', backgroundColor: b.bg, color: b.text, fontSize: '12px', fontWeight: '600' }}>
      {score === null || score === undefined ? '—' : score}
    </div>
  );
}

function HttpStatusCell({ status, redirectUrl }) {
  const is2xx = status >= 200 && status < 300;
  const is3xx = status >= 300 && status < 400;
  const label = status || 'Unknown';
  const bg = is2xx ? '#e0f5f3' : is3xx ? '#fffbeb' : '#fee2e2';
  const text = is2xx ? colors.green : is3xx ? '#854F0B' : '#dc2626';
  const title = is3xx && redirectUrl ? `Redirects to ${redirectUrl}` : status ? `HTTP ${status}` : 'Unable to determine HTTP status';

  return (
    <div title={title} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '54px', padding: '3px 7px', borderRadius: '6px', backgroundColor: bg, color: text, fontSize: '12px', fontWeight: '700' }}>
      {label}
    </div>
  );
}

function NotAuditableCell({ reason }) {
  return (
    <div title={`Not auditable — ${reason || 'HTTP status unavailable'}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', borderRadius: '6px', backgroundColor: '#fee2e2', color: '#dc2626' }}>
      <i className="ti ti-alert-triangle" style={{ fontSize: '15px' }} aria-label={`Not auditable — ${reason || 'HTTP status unavailable'}`} />
    </div>
  );
}
function RecsBlock({ recs }) {
  if (!recs || recs.length === 0) return null;
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Recommendations</div>
      {recs.map((r, i) => {
        const isCrit = r.includes('🔴') || r.includes('Missing') || r.includes('No ');
        const isGood = r.includes('✅');
        return (
          <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', marginBottom: '6px', backgroundColor: isCrit ? 'rgba(231,111,81,0.08)' : isGood ? 'rgba(1,121,111,0.05)' : 'rgba(233,196,106,0.08)', border: `1px solid ${isCrit ? colors.grey : isGood ? colors.green : colors.tertiary}` }}>
            <p style={{ margin: 0, fontSize: '13px', color: isCrit ? '#993C1D' : isGood ? colors.green : '#854F0B' }}>{r}</p>
          </div>
        );
      })}
    </div>
  );
}
function StatBox({ label, value, color, bg, border }) {
  return (
    <div style={{ padding: '12px', backgroundColor: bg || '#f9fafb', borderRadius: '8px', border: `1px solid ${border || '#e5e7eb'}` }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: color || '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: '700', color: color || colors.primary }}>{value}</div>
    </div>
  );
}
// ---- Schema drawer ----
function SchemaDrawerBody({ urlData }) {
  const [expandedSchema, setExpandedSchema] = useState(null);
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No schema data for this URL.</p>;
  const correct = urlData.correctSchemas || [];
  const malformed = urlData.malformedSchemas || [];
  const missing = urlData.missingSchemas || [];
  const issues = urlData.schemaIssues || [];
  const recs = urlData.recommendations || [];
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '24px' }}>
        <StatBox label="Correct" value={correct.length} color={colors.green} bg="#e0f5f3" border={colors.green} />
        <StatBox label="Needs Fixing" value={malformed.length} color="#854F0B" bg="#fffbeb" border={colors.tertiary} />
        <StatBox label="Missing" value={missing.length} color="#993C1D" bg="#fee2e2" border="#fca5a5" />
      </div>
      {correct.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.green, marginBottom: '8px' }}>✓ Correct ({correct.length})</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {correct.map((t, i) => <span key={i} style={{ padding: '5px 10px', borderRadius: '6px', backgroundColor: 'rgba(1,121,111,0.05)', border: `1px solid ${colors.green}`, fontSize: '12px', color: colors.green }}>{t}</span>)}
          </div>
        </div>
      )}
      {malformed.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#854F0B', marginBottom: '8px' }}>⚠️ Needs fixing ({malformed.length})</div>
          {malformed.map((t, i) => {
            const isExpanded = expandedSchema === t;
            const schemaIssues = issues.filter(iss => iss.schemaType === t || iss.schemaType === 'Unknown');
            return (
              <div key={i} style={{ marginBottom: '6px' }}>
                <button onClick={() => setExpandedSchema(isExpanded ? null : t)} style={{ padding: '8px 12px', width: '100%', textAlign: 'left', borderRadius: '6px', backgroundColor: isExpanded ? colors.tertiary : 'rgba(233,196,106,0.05)', border: `2px solid ${colors.tertiary}`, color: isExpanded ? 'white' : '#854F0B', fontSize: '13px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t}</span><span>{isExpanded ? '▼' : '▶'}</span>
                </button>
                {isExpanded && (
                  <div style={{ marginTop: '6px', padding: '12px', backgroundColor: '#fffbeb', border: `1px solid ${colors.tertiary}`, borderRadius: '6px', fontSize: '13px' }}>
                    {schemaIssues.length > 0 ? schemaIssues.map((iss, j) => (
                      <div key={j} style={{ marginBottom: '8px' }}>
                        <div style={{ color: '#78350f' }}>• {iss.message}</div>
                        {iss.fix && <div style={{ color: '#78350f', paddingLeft: '8px', marginTop: '4px' }}>🔧 {iss.fix}</div>}
                      </div>
                    )) : <div style={{ color: '#78350f' }}>Validation errors found. Review {t} markup for required properties.</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {missing.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.grey, marginBottom: '8px' }}>✗ Missing ({missing.length})</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {missing.map((t, i) => <span key={i} style={{ padding: '5px 10px', borderRadius: '6px', backgroundColor: 'rgba(231,111,81,0.05)', border: `1px solid ${colors.grey}`, fontSize: '12px', color: colors.grey }}>{t}</span>)}
          </div>
        </div>
      )}
      <RecsBlock recs={recs} />
    </>
  );
}
// ---- Freshness drawer ----
function FreshnessDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No freshness data for this URL.</p>;
  const formatDate = (d) => {
    if (!d) return 'Not found';
    try { return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return 'Invalid date'; }
  };
  const statusColor = (s) => { const u = s?.toUpperCase(); if (u === 'FRESH') return colors.green; if (u === 'AGING') return '#854F0B'; return colors.grey; };
  const statusBg = (s) => { const u = s?.toUpperCase(); if (u === 'FRESH') return '#e0f5f3'; if (u === 'AGING') return '#fffbeb'; return '#fee2e2'; };
  const cat = urlData.freshnessCategory || urlData.status || 'UNKNOWN';
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <StatBox label="Published" value={formatDate(urlData.publishDate)} />
        <StatBox label="Modified" value={formatDate(urlData.modifiedDate)} />
      </div>
      <div style={{ padding: '14px', borderRadius: '8px', backgroundColor: statusBg(cat), border: `1px solid ${statusColor(cat)}`, marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: '600', color: statusColor(cat), marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Content age</div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: statusColor(cat) }}>{urlData.ageInDays ? `${urlData.ageInDays} days` : 'Unknown'}</div>
        </div>
        <div style={{ fontSize: '28px', opacity: 0.3 }}>{urlData.isStale ? '⚠️' : '✓'}</div>
      </div>
      {urlData.datesSources && urlData.datesSources.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Date sources found ({urlData.datesSources.length})</div>
          {urlData.datesSources.map((s, i) => (
            <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: 'rgba(1,121,111,0.05)', border: `1px solid ${colors.green}`, marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: colors.green }}>{s.source}</span>
              <span style={{ fontSize: '13px', color: colors.green }}>{formatDate(s.date)}</span>
            </div>
          ))}
        </div>
      )}
      {!urlData.hasTimestamp && (
        <div style={{ padding: '14px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '2px solid #fca5a5', marginBottom: '20px' }}>
          <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '6px', fontSize: '13px' }}>No date markup found</div>
          <div style={{ fontSize: '13px', color: '#7f1d1d' }}>LLMs cannot determine content freshness. This severely impacts citation likelihood for time-sensitive queries.</div>
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Disambiguation drawer ----
function DisambiguationDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No disambiguation data for this URL.</p>;
  const sameAs = urlData.sameAsUrls || [];
  const authorities = urlData.matchedAuthorities || [];
  const hasJsonLd = urlData.hasJsonLd;
  const hasOrg = urlData.hasOrganisationEntity;
  const hasSameAs = urlData.hasSameAs;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <StatBox label="JSON-LD" value={hasJsonLd ? '✓' : '✗'} color={hasJsonLd ? colors.green : colors.grey} bg={hasJsonLd ? '#e0f5f3' : '#fee2e2'} border={hasJsonLd ? colors.green : '#fca5a5'} />
        <StatBox label="Org Entity" value={hasOrg ? '✓' : '✗'} color={hasOrg ? colors.green : colors.grey} bg={hasOrg ? '#e0f5f3' : '#fee2e2'} border={hasOrg ? colors.green : '#fca5a5'} />
        <StatBox label="sameAs" value={sameAs.length} color={sameAs.length > 0 ? colors.green : colors.grey} bg={sameAs.length > 0 ? '#e0f5f3' : '#fee2e2'} border={sameAs.length > 0 ? colors.green : '#fca5a5'} />
      </div>
      {authorities.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.green, marginBottom: '8px' }}>✓ Authority links found ({authorities.length})</div>
          {authorities.map((a, i) => (
            <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(1,121,111,0.05)', border: `1px solid ${colors.green}`, marginBottom: '6px', fontSize: '12px', color: colors.green, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{a}</div>
          ))}
        </div>
      )}
      {sameAs.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>sameAs URLs ({sameAs.length})</div>
          {sameAs.map((u, i) => (
            <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '6px', fontSize: '12px', color: colors.secondary, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{u}</div>
          ))}
        </div>
      )}
      {!hasSameAs && (
        <div style={{ padding: '14px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '2px solid #fca5a5', marginBottom: '16px' }}>
          <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '6px', fontSize: '13px' }}>No sameAs links found</div>
          <div style={{ fontSize: '13px', color: '#7f1d1d' }}>Without sameAs anchors, AI engines cannot reliably connect this page to your entity in the knowledge graph.</div>
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Crosslinking drawer ----
function CrosslinkingDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No crosslinking data for this URL.</p>;
  const inbound = urlData.inboundLinks || [];
  const count = urlData.inboundCount || inbound.length || 0;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <StatBox label="Inbound links" value={count} color={count >= 3 ? colors.green : count >= 1 ? '#854F0B' : colors.grey} bg={count >= 3 ? '#e0f5f3' : count >= 1 ? '#fffbeb' : '#fee2e2'} border={count >= 3 ? colors.green : count >= 1 ? colors.tertiary : '#fca5a5'} />
        <StatBox label="From campaign URLs" value={inbound.length} />
      </div>
      {inbound.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Linking pages</div>
          {inbound.map((link, i) => (
            <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(1,121,111,0.05)', border: `1px solid ${colors.green}`, marginBottom: '6px', fontSize: '12px', color: colors.green, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{link}</div>
          ))}
        </div>
      )}
      {count === 0 && (
        <div style={{ padding: '14px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '2px solid #fca5a5', marginBottom: '16px' }}>
          <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '6px', fontSize: '13px' }}>No inbound internal links</div>
          <div style={{ fontSize: '13px', color: '#7f1d1d' }}>No other pages in this campaign link to this URL. Internal linking signals content importance to AI crawlers.</div>
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Fact density drawer ----
function FactDensityDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No fact density data for this URL.</p>;
  const factDensity = urlData.factDensity;
  const factCount = urlData.factCount || urlData.totalFacts;
  const wordCount = urlData.wordCount;
  const benchmark = 2.5;
  const pct = factDensity != null ? Math.min(100, Math.round((factDensity / benchmark) * 100)) : null;
  const b = band(pct);
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <StatBox label="Facts found" value={factCount != null ? factCount : '—'} />
        <StatBox label="Word count" value={wordCount != null ? wordCount.toLocaleString() : '—'} />
        <StatBox label="Facts / 100w" value={factDensity != null ? factDensity.toFixed(1) : '—'} color={b.text} bg={b.bg} border={b.bar} />
      </div>
      {factDensity != null && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>Density vs benchmark (2.5 facts/100w)</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: b.text }}>{pct}%</span>
          </div>
          <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: b.bar, borderRadius: '4px' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>0</span>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Benchmark: 2.5</span>
          </div>
        </div>
      )}
      {urlData.facts && urlData.facts.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Sample facts detected</div>
          {urlData.facts.slice(0, 5).map((f, i) => (
            <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '6px', fontSize: '13px', color: colors.primary }}>• {f}</div>
          ))}
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Headings drawer ----
function HeadingsDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No headings data for this URL.</p>;
  const issues = urlData.issues || [];
  const headings = urlData.headings || [];
  const hasH1 = urlData.hasH1;
  const h1Count = urlData.h1Count || 0;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <StatBox label="H1 present" value={hasH1 ? '✓' : '✗'} color={hasH1 ? colors.green : colors.grey} bg={hasH1 ? '#e0f5f3' : '#fee2e2'} border={hasH1 ? colors.green : '#fca5a5'} />
        <StatBox label="H1 count" value={h1Count} color={h1Count === 1 ? colors.green : h1Count === 0 ? colors.grey : '#854F0B'} bg={h1Count === 1 ? '#e0f5f3' : h1Count === 0 ? '#fee2e2' : '#fffbeb'} border={h1Count === 1 ? colors.green : h1Count === 0 ? '#fca5a5' : colors.tertiary} />
        <StatBox label="Issues" value={issues.length} color={issues.length === 0 ? colors.green : colors.grey} bg={issues.length === 0 ? '#e0f5f3' : '#fee2e2'} border={issues.length === 0 ? colors.green : '#fca5a5'} />
      </div>
      {issues.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.grey, marginBottom: '8px' }}>Issues found</div>
          {issues.map((iss, i) => (
            <div key={i} style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: 'rgba(231,111,81,0.08)', border: `1px solid ${colors.grey}`, marginBottom: '6px', fontSize: '13px', color: '#993C1D' }}>• {iss}</div>
          ))}
        </div>
      )}
      {headings.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Heading structure</div>
          {headings.slice(0, 10).map((h, i) => (
            <div key={i} style={{ padding: '6px 12px', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '4px', display: 'flex', gap: '10px', alignItems: 'baseline' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: colors.secondary, minWidth: '24px' }}>{h.tag?.toUpperCase() || 'H?'}</span>
              <span style={{ fontSize: '13px', color: colors.primary }}>{h.text}</span>
            </div>
          ))}
          {headings.length > 10 && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>+{headings.length - 10} more headings</div>}
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Structured elements drawer ----
function StructuredElementsDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No structured elements data for this URL.</p>;
  const tableCount = urlData.tableCount || 0;
  const listCount = urlData.listCount || urlData.listItemCount || 0;
  const statCount = urlData.statCount || urlData.statisticsCount || 0;
  const elements = urlData.elements || urlData.structuredElements || [];
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
        <StatBox label="Tables" value={tableCount} color={tableCount > 0 ? colors.green : '#6b7280'} bg={tableCount > 0 ? '#e0f5f3' : '#f9fafb'} border={tableCount > 0 ? colors.green : '#e5e7eb'} />
        <StatBox label="Lists" value={listCount} color={listCount > 0 ? colors.green : '#6b7280'} bg={listCount > 0 ? '#e0f5f3' : '#f9fafb'} border={listCount > 0 ? colors.green : '#e5e7eb'} />
        <StatBox label="Stats / data" value={statCount} color={statCount > 0 ? colors.green : '#6b7280'} bg={statCount > 0 ? '#e0f5f3' : '#f9fafb'} border={statCount > 0 ? colors.green : '#e5e7eb'} />
      </div>
      {elements.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Elements detected</div>
          {elements.map((el, i) => (
            <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(1,121,111,0.05)', border: `1px solid ${colors.green}`, marginBottom: '6px', fontSize: '13px', color: colors.primary }}>• {typeof el === 'string' ? el : el.type || el.label || JSON.stringify(el)}</div>
          ))}
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Citation density drawer ----
function CitationDensityDrawerBody({ urlData }) {
  if (!urlData) return <p style={{ color: '#6b7280', fontSize: '13px' }}>No citation density data for this URL.</p>;
  const links = urlData.authorityLinks || [];
  const count = urlData.authorityLinkCount || links.length || 0;
  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <StatBox label="Authority links found" value={count} color={count >= 6 ? colors.green : count >= 3 ? '#854F0B' : colors.grey} bg={count >= 6 ? '#e0f5f3' : count >= 3 ? '#fffbeb' : '#fee2e2'} border={count >= 6 ? colors.green : count >= 3 ? colors.tertiary : '#fca5a5'} />
      </div>
      {count === 0 && (
        <div style={{ padding: '14px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '2px solid #fca5a5', marginBottom: '16px' }}>
          <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '6px', fontSize: '13px' }}>No authority citations found</div>
          <div style={{ fontSize: '13px', color: '#7f1d1d' }}>Pages without links to authoritative sources are harder for AI engines to verify and less likely to be cited.</div>
        </div>
      )}
      {links.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>Authority links ({links.length})</div>
          {links.map((link, i) => {
            const url = typeof link === 'string' ? link : link.url || link.href || '';
            const label = typeof link === 'string' ? link : link.text || link.anchor || url;
            return (
              <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(1,121,111,0.05)', border: `1px solid ${colors.green}`, marginBottom: '6px' }}>
                {label !== url && <div style={{ fontSize: '12px', fontWeight: '600', color: colors.primary, marginBottom: '2px' }}>{label}</div>}
                <div style={{ fontSize: '12px', color: colors.green, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{url}</div>
              </div>
            );
          })}
        </div>
      )}
      <RecsBlock recs={urlData.recommendations} />
    </>
  );
}
// ---- Hallucination risk drawer ----
function HallucinationRiskDrawerBody({ rowScores }) {
  const inputs = [
    { label: 'Disambiguation', key: 'disambiguation', weight: '40%', description: 'Unanchored entity — engine has to guess' },
    { label: 'Citation density', key: 'citationDensity', weight: '30%', description: 'No citations — claims unverifiable' },
    { label: 'Schema', key: 'schema', weight: '20%', description: 'No structured facts for engines to parse' },
    { label: 'Freshness', key: 'freshness', weight: '10%', description: 'Stale content — higher risk of outdated claims' }
  ];
  const overallRisk = rowScores.hallucinationRisk;
  const riskBand = band(overallRisk ? 100 - overallRisk : null);
  return (
    <>
      <div style={{ padding: '14px', borderRadius: '8px', backgroundColor: overallRisk >= 70 ? '#fee2e2' : overallRisk >= 45 ? '#fffbeb' : '#e0f5f3', border: `2px solid ${overallRisk >= 70 ? '#fca5a5' : overallRisk >= 45 ? colors.tertiary : colors.green}`, marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: '600', color: overallRisk >= 70 ? '#dc2626' : overallRisk >= 45 ? '#854F0B' : colors.green, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Overall hallucination risk</div>
        <div style={{ fontSize: '28px', fontWeight: '700', color: overallRisk >= 70 ? '#dc2626' : overallRisk >= 45 ? '#854F0B' : colors.green }}>{overallRisk != null ? overallRisk + '%' : '—'}</div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Higher score = higher risk. Derived from four GEO signals below.</div>
      </div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '10px' }}>Contributing signals</div>
      {inputs.map(inp => {
        const score = rowScores[inp.key];
        const contribution = score != null ? Math.round(100 - score) : null;
        const b = band(score);
        return (
          <div key={inp.key} style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{inp.label}</span>
                <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>({inp.weight} weight)</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {score != null && <span style={{ fontSize: '11px', color: '#6b7280' }}>Score: {score}</span>}
                <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: b.bg, color: b.text, fontSize: '12px', fontWeight: '600' }}>
                  {contribution != null ? `+${contribution} risk` : 'Not run'}
                </span>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{inp.description}</div>
            {score != null && (
              <div style={{ marginTop: '8px', height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: (100 - score) + '%', height: '100%', background: b.bar }} />
              </div>
            )}
          </div>
        );
      })}
      <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(4,139,168,0.05)', border: `1px solid ${colors.secondary}`, fontSize: '12px', color: '#1e40af' }}>
        💡 Fix disambiguation and citation density first — together they account for 70% of this score.
      </div>
    </>
  );
}
// ---- Crawlability drawer ----
function CrawlabilityDrawerBody({ urlData, crawlable }) {
  if (crawlable) {
    return (
      <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: '#e0f5f3', border: `1px solid ${colors.green}` }}>
        <div style={{ fontWeight: '600', color: colors.green, marginBottom: '6px', fontSize: '13px' }}>✓ Page is crawlable</div>
        <div style={{ fontSize: '13px', color: '#065f46' }}>This page was successfully fetched without requiring JavaScript rendering. AI crawlers can access its content directly.</div>
      </div>
    );
  }
  return (
    <>
      <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '2px solid #fca5a5', marginBottom: '16px' }}>
        <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '6px', fontSize: '13px' }}>⚠️ JavaScript rendering required</div>
        <div style={{ fontSize: '13px', color: '#7f1d1d' }}>This page requires JavaScript to render its content. Many AI crawlers — including those used by Perplexity, ChatGPT, and Gemini — do not execute JavaScript. Content may be invisible to them.</div>
      </div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>What this means</div>
      {[
        'Schema markup embedded in JS may not be read',
        'Body content may appear blank to AI crawlers',
        'Citation and fact density scores are unreliable',
        'All other Content Health checks are marked as not run'
      ].map((item, i) => (
        <div key={i} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(231,111,81,0.08)', border: `1px solid ${colors.grey}`, marginBottom: '6px', fontSize: '13px', color: '#993C1D' }}>• {item}</div>
      ))}
      <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(4,139,168,0.05)', border: `1px solid ${colors.secondary}`, fontSize: '12px', color: '#1e40af' }}>
        💡 Consider adding server-side rendering (SSR) or static generation for key pages to ensure AI crawlability.
      </div>
    </>
  );
}
// ---- Drawer shell ----
function Drawer({ open, onClose, title, badge, badgeBg, badgeColor, url, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div role="dialog" aria-label={title} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 92vw)', backgroundColor: 'white', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: 1001, display: 'flex', flexDirection: 'column', fontFamily: "'Work Sans', sans-serif" }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '17px', fontWeight: '700', color: colors.primary }}>{title}</span>
              {badge && <span style={{ padding: '3px 10px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>{badge}</span>}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all', lineHeight: 1.5 }}>{url}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {children}
        </div>
      </div>
    </>
  );
}
// ---- Main component ----
export default function ContentHealthTable({ campaign, apiBaseUrl }) {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState(null);
  // Per-URL detail data keyed by module type
  const [detailCache, setDetailCache] = useState({});

  const downloadCSV = () => {
    if (!matrix || !matrix.rows) return;

    // CSV header
    const headers = ['URL', 'HTTP Status', ...CHECKS.map(c => c.label)];
    const csvContent = [headers.join(',')];

    // CSV rows
    matrix.rows.forEach(row => {
      const urlCell = `"${row.url.replace(/"/g, '""')}"`;
      const statusCell = row.httpStatus ?? 'Unknown';
      const scores = CHECKS.map(c => row.httpAuditable ? (row.scores[c.key] ?? '—') : `Not auditable — ${row.httpStatusReason || 'HTTP status unavailable'}`);
      csvContent.push([urlCell, statusCell, ...scores].join(','));
    });

    // Download
    const csv = csvContent.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `content-health-${campaign.id}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  useEffect(() => {
    if (!campaign?.id) { setLoading(false); return; }
    fetch(`${apiBaseUrl}/campaigns/${campaign.id}/health-matrix`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setMatrix(d);
        else setError(d.error || 'Failed to load health matrix');
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [campaign?.id, apiBaseUrl]);
  const MODULE_MAP = {
    schema:             'schema-audit',
    freshness:          'freshness',
    headings:           'heading-audit',
    disambiguation:     'disambiguation',
    crosslinking:       'crosslinking',
    factDensity:        'content-comprehensiveness-audit',
    structuredElements: 'structured-content-audit',
    citationDensity:    'citation-density',
  };
  const openDrawer = async (url, check, rowScores, crawlable) => {
    setDrawer({ url, check, rowScores, crawlable });
    const moduleType = MODULE_MAP[check];
    if (!moduleType) return;
    const cacheKey = `${moduleType}`;
    if (detailCache[cacheKey]) return;
    try {
      const r = await fetch(`${apiBaseUrl}/audit-results/latest?campaignId=${campaign.id}&moduleType=${moduleType}`);
      const d = await r.json();
      const records = d.results || [];
      const map = {};
      for (const rec of records) {
        const rd = rec.result_data;
        if (Array.isArray(rd?.urls)) {
          for (const u of rd.urls) { if (u?.url) map[u.url] = u; }
        } else if (Array.isArray(rd?.results)) {
          for (const u of rd.results) { if (u?.url) map[u.url] = u; }
        } else if (rec.url) {
          map[rec.url] = rd;
        }
      }
      setDetailCache(prev => ({ ...prev, [cacheKey]: map }));
    } catch {}
  };
  const closeDrawer = () => setDrawer(null);
  const getDrawerBadge = (check, score) => {
    if (check === 'hallucinationRisk') {
      const b = band(score != null ? 100 - score : null);
      const label = score === null ? 'Not run' : score >= 70 ? `${score}% — High risk` : score >= 45 ? `${score}% — Medium risk` : `${score}% — Low risk`;
      return { badge: label, badgeBg: score >= 70 ? '#fee2e2' : score >= 45 ? '#fffbeb' : '#e0f5f3', badgeColor: score >= 70 ? '#dc2626' : score >= 45 ? '#854F0B' : colors.green };
    }
    const b = band(score);
    const label = score === null ? 'Not run' : `${score}% — ${score >= 70 ? 'Strong' : score >= 45 ? 'Adequate' : 'Needs work'}`;
    return { badge: label, badgeBg: b.bg, badgeColor: b.text };
  };
  if (!campaign) return (
    <div style={{ padding: '24px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
      <p style={{ color: '#78350f', margin: 0, fontSize: '14px' }}>Select a campaign to view content health.</p>
    </div>
  );
  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center' }}>
      <div style={{ width: '36px', height: '36px', border: '4px solid #e5e7eb', borderTop: `4px solid ${colors.secondary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading content health...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (error) return (
    <div style={{ padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', border: '1px solid #ef4444' }}>
      <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
    </div>
  );
  if (!matrix || !matrix.rows || matrix.rows.length === 0) return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>No audit data yet</h3>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Run a content health audit to populate this table.</p>
    </div>
  );
  const healthBand = band(matrix.overallHealth);
  return (
    <div style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '2px' }}>Content health</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>{campaign.name} · {matrix.rows.length} URLs</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button onClick={downloadCSV} style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: colors.secondary, color: 'white', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = '0.9'} onMouseLeave={e => e.target.style.opacity = '1'}>
            <i className="ti ti-download" style={{ fontSize: '15px' }} />
            Download CSV
          </button>
          {!matrix.crawlabilityGate.reliable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#92400e', backgroundColor: '#fef3c7', padding: '6px 12px', borderRadius: '8px' }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: '15px' }} />
              {matrix.crawlabilityGate.failedUrls} URL{matrix.crawlabilityGate.failedUrls > 1 ? 's are' : ' is'} not fully auditable
            </div>
          )}
          {matrix.overallHealth !== null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Overall health</div>
              <div style={{ fontSize: '26px', fontWeight: '700', color: healthBand.text }}>{matrix.overallHealth}%</div>
            </div>
          )}
        </div>
      </div>
      {/* Ten-tile summary grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        {CHECKS.map(c => {
          const scores = matrix.rows.map(r => r.scores[c.key]).filter(s => s !== null && s !== undefined);
          const avg = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
          const b = band(avg);
          return (
            <div key={c.key} style={{ backgroundColor: 'white', border: '0.5px solid #e5e7eb', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className={`ti ${c.icon}`} style={{ fontSize: '15px', color: avg !== null ? b.text : '#9ca3af' }} aria-hidden='true' />
                <span style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>{c.label}</span>
              </div>
              <div style={{ fontSize: '20px', fontWeight: '500', color: avg !== null ? b.text : '#9ca3af' }}>{avg !== null ? avg + '%' : '—'}</div>
              <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: avg !== null ? avg + '%' : '0%', height: '100%', background: avg !== null ? b.bar : '#e5e7eb' }} />
              </div>
            </div>
          );
        })}
      </div>
      {/* Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 2, whiteSpace: 'nowrap' }}>URL</th>
                <th title="Current HTTP status" style={{ padding: '12px 8px', textAlign: 'center', color: '#6b7280', borderLeft: '1px solid #f3f4f6', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>HTTP Status</th>
                {CHECKS.map(c => (
                  <th key={c.key} title={c.label} style={{ padding: '12px 8px', textAlign: 'center', color: '#6b7280', borderLeft: '1px solid #f3f4f6' }}>
                    <i className={`ti ${c.icon}`} style={{ fontSize: '17px' }} aria-label={c.label} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row, idx) => {
                const httpAuditable = row.httpAuditable === true;
                const rowBg = !httpAuditable ? '#fff7f7' : !row.crawlable ? '#fffdf5' : 'white';
                return (
                  <tr key={idx} style={{ borderBottom: idx < matrix.rows.length - 1 ? '1px solid #f3f4f6' : 'none', backgroundColor: rowBg }}
                    onMouseEnter={e => { if (httpAuditable && row.crawlable) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = rowBg; }}>
                    <td style={{ padding: '10px 16px', position: 'sticky', left: 0, backgroundColor: 'inherit', zIndex: 1, whiteSpace: 'nowrap', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.url}>
                      <span style={{ fontSize: '12px', fontFamily: 'ui-monospace, monospace', color: colors.primary }}>{truncateUrl(row.url)}</span>
                      {httpAuditable && !row.crawlable && <span style={{ marginLeft: '8px', fontSize: '11px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>JS</span>}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #f3f4f6' }}>
                      <HttpStatusCell status={row.httpStatus} redirectUrl={row.redirectUrl} />
                    </td>
                    {CHECKS.map(c => {
                      const score = row.scores[c.key];
                      const isClickable = httpAuditable && score !== null && score !== undefined;
                      return (
                        <td key={c.key} style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #f3f4f6', cursor: isClickable ? 'pointer' : 'default' }}
                          onClick={isClickable ? () => openDrawer(row.url, c.key, row.scores, row.crawlable) : undefined}
                          title={isClickable ? `View ${c.label} detail` : !httpAuditable ? `Not auditable — ${row.httpStatusReason || 'HTTP status unavailable'}` : undefined}>
                          {httpAuditable ? <ScoreCell score={score} /> : <NotAuditableCell reason={row.httpStatusReason} />}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Icon legend */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {CHECKS.map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280' }}>
              <i className={`ti ${c.icon}`} style={{ fontSize: '14px' }} />
              <span>{c.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Banding key */}
      <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap' }}>
        {[
          { label: 'Strong (≥70)', bg: 'rgba(1,121,111,0.08)', color: '#01796f' },
          { label: 'Adequate (45–69)', bg: 'rgba(233,196,106,0.15)', color: '#854F0B' },
          { label: 'Needs work (<45)', bg: 'rgba(231,111,81,0.08)', color: '#993C1D' },
          { label: 'Not run', bg: '#f3f4f6', color: '#9ca3af' },
          { label: 'Not auditable', bg: '#fee2e2', color: '#dc2626' }
        ].map(({ label, bg, color }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
            <div style={{ width: '24px', height: '16px', borderRadius: '4px', backgroundColor: bg, border: `1px solid ${color}20` }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
      {/* Drawer */}
      {drawer && (() => {
        const { url, check, rowScores, crawlable } = drawer;
        const score = rowScores[check];
        const checkMeta = CHECKS.find(c => c.key === check);
        const { badge, badgeBg, badgeColor } = getDrawerBadge(check, score);
        const moduleType = MODULE_MAP[check];
        const urlData = moduleType && detailCache[moduleType] ? detailCache[moduleType][url] : null;
        return (
          <Drawer open={true} onClose={closeDrawer} title={checkMeta?.label || check} badge={badge} badgeBg={badgeBg} badgeColor={badgeColor} url={url}>
            {check === 'schema'             && <SchemaDrawerBody urlData={urlData} />}
            {check === 'freshness'          && <FreshnessDrawerBody urlData={urlData} />}
            {check === 'disambiguation'     && <DisambiguationDrawerBody urlData={urlData} />}
            {check === 'crosslinking'       && <CrosslinkingDrawerBody urlData={urlData} />}
            {check === 'factDensity'        && <FactDensityDrawerBody urlData={urlData} />}
            {check === 'headings'           && <HeadingsDrawerBody urlData={urlData} />}
            {check === 'structuredElements' && <StructuredElementsDrawerBody urlData={urlData} />}
            {check === 'citationDensity'    && <CitationDensityDrawerBody urlData={urlData} />}
            {check === 'hallucinationRisk'  && <HallucinationRiskDrawerBody rowScores={rowScores} />}
            {check === 'crawlability'       && <CrawlabilityDrawerBody crawlable={crawlable} />}
          </Drawer>
        );
      })()}
    </div>
  );
}
